import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import LoadingSpinner from '../components/LoadingSpinner';

const ROLES = ['Student', 'Instructor', 'Admin', 'Coordinator', 'Registrar', 'Super - Admin', 'System Admin', 'Super - System Admin'];

const ActionLink = ({ onSuccess, onError }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkData, setLinkData] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No link token provided. Please use the link that was sent to you.');
      setLoading(false);
      return;
    }

    const fetchLink = async () => {
      try {
        const res = await axios.get(`/api/temp-links/${token}`);
        setLinkData(res.data);
        setError(null);
      } catch (err) {
        const msg = err.response?.data?.error || err.message;
        setError(msg);
        setLinkData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLink();
  }, [token]);

  if (loading) {
    return (
      <div style={styles.centerPage}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error && !linkData) {
    return (
      <div style={styles.centerPage}>
        <div style={styles.card}>
          <h1 style={styles.title}>Invalid or Expired Link</h1>
          <p style={styles.errorText}>{error}</p>
          <p style={styles.hint}>If you believe this is an error, please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (linkData?.action === 'reset-password') {
    return (
      <ResetPasswordForm
        token={token}
        onSuccess={() => onSuccess?.('Password updated successfully. You can now log in.')}
        onError={(msg) => { setError(msg); onError?.(msg); }}
      />
    );
  }

  if (linkData?.action === 'create-account') {
    return (
      <CreateAccountForm
        token={token}
        metadata={linkData.metadata || {}}
        onSuccess={() => onSuccess?.('Account created successfully. You can now log in.')}
        onError={(msg) => { setError(msg); onError?.(msg); }}
      />
    );
  }

  return null;
};

function ResetPasswordForm({ token, onSuccess, onError }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    onError(null);

    if (password.length < 8) {
      onError('Password must be at least 8 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      onError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/temp-links/reset-password', {
        token,
        newPassword: password
      });
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.centerPage}>
      <div style={styles.card}>
        <h1 style={styles.title}>Reset Your Password</h1>
        <p style={styles.subtitle}>Enter your new password below.</p>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>New Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              style={styles.input}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm Password *</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              style={styles.input}
            />
          </div>
          <button type="submit" disabled={loading} style={styles.submitButton}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

function CreateAccountForm({ token, metadata, onSuccess, onError }) {
  const [lookups, setLookups] = useState({ schools: [], concentrations: [], associations: [], periods: [] });
  const [loadingLookups, setLoadingLookups] = useState(true);
  // Fields preloaded from metadata should be disabled (visitor cannot modify)
  const preloaded = {
    first_name: !!(metadata.firstName ?? metadata.first_name),
    last_name: !!(metadata.lastName ?? metadata.last_name),
    primary_email: !!(metadata.email),
    dob: !!(metadata.dob),
    accesscard_email: !!(metadata.accesscard_email ?? metadata.email),
    role: !!(metadata.role),
    school_id: !!(metadata.school_id),
    global: 'global' in metadata,
    start_date: !!(metadata.start_date),
    end_date: !!(metadata.end_date),
    gradelevel: !!(metadata.gradelevel),
    concentration: !!(metadata.concentration),
    association: !!(metadata.association),
    period_id: !!(metadata.period_id)
  };
  const [formData, setFormData] = useState({
    first_name: metadata.firstName || metadata.first_name || '',
    last_name: metadata.lastName || metadata.last_name || '',
    primary_email: metadata.email || '',
    dob: metadata.dob ? (metadata.dob.split?.('T')?.[0] || metadata.dob) : '',
    signature: '',
    password: '',
    accesscard_email: metadata.accesscard_email || metadata.email || '',
    role: metadata.role || 'Student',
    school_id: metadata.school_id || '',
    global: metadata.global || false,
    start_date: metadata.start_date || '',
    end_date: metadata.end_date || '',
    gradelevel: metadata.gradelevel || '',
    concentration: metadata.concentration || '',
    association: metadata.association || '',
    period_id: metadata.period_id || ''
  });
  const [loading, setLoading] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const signaturePadRef = useRef(null);

  useEffect(() => {
    axios.get('/api/temp-links/lookups')
      .then(res => setLookups(res.data))
      .catch(() => setLookups({ schools: [], concentrations: [], associations: [], periods: [] }))
      .finally(() => setLoadingLookups(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    onError(null);

    let signatureData = formData.signature;
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      signatureData = signaturePadRef.current.toDataURL('image/png');
    }

    setLoading(true);
    try {
      await axios.post('/api/temp-links/create-account', {
        token,
        account: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          primary_email: formData.primary_email,
          dob: formData.dob || null,
          signature: signatureData,
          password: formData.password
        },
        accesscard: {
          email: formData.accesscard_email,
          role: formData.role,
          school_id: formData.school_id || null,
          global: formData.global
        },
        enrollment: formData.role === 'Student' ? {
          start_date: formData.start_date,
          end_date: formData.end_date,
          gradelevel: formData.gradelevel,
          concentration: formData.concentration,
          association: formData.association,
          period_id: formData.period_id
        } : null
      });
      onSuccess();
    } catch (err) {
      onError(err.response?.data?.error || 'Failed to create account.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingLookups) {
    return (
      <div style={styles.centerPage}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={styles.centerPage}>
      <div style={{ ...styles.card, maxWidth: 600 }}>
        <h1 style={styles.title}>Create Your Account</h1>
        <p style={styles.subtitle}>Complete the form below to set up your account.</p>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>First Name *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
              disabled={preloaded.first_name}
              style={{ ...styles.input, ...(preloaded.first_name && styles.inputDisabled) }}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Last Name *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
              disabled={preloaded.last_name}
              style={{ ...styles.input, ...(preloaded.last_name && styles.inputDisabled) }}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email *</label>
            <input
              type="email"
              value={formData.primary_email}
              onChange={(e) => setFormData({ ...formData, primary_email: e.target.value })}
              required
              disabled={preloaded.primary_email}
              style={{ ...styles.input, ...(preloaded.primary_email && styles.inputDisabled) }}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Date of Birth</label>
            <input
              type="date"
              value={formData.dob}
              onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
              disabled={preloaded.dob}
              style={{ ...styles.input, ...(preloaded.dob && styles.inputDisabled) }}
            />
          </div>
          <div style={styles.inputGroup}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={styles.label}>Signature</label>
              <button
                type="button"
                onClick={() => setShowSignaturePad(!showSignaturePad)}
                style={styles.smallButton}
              >
                {showSignaturePad ? 'Hide' : 'Show'} Signature Pad
              </button>
            </div>
            {showSignaturePad && (
              <div style={{ border: '1px solid #dee2e6', borderRadius: 4, padding: 8 }}>
                <SignatureCanvas
                  ref={signaturePadRef}
                  canvasProps={{ style: { width: '100%', height: 120, border: '1px solid #ccc', borderRadius: 4 } }}
                />
                <button type="button" onClick={() => signaturePadRef.current?.clear()} style={styles.smallButton}>
                  Clear
                </button>
              </div>
            )}
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Password *</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              minLength={8}
              autoComplete="new-password"
              style={styles.input}
            />
          </div>

          <h3 style={styles.sectionTitle}>Access Card</h3>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Access Card Email *</label>
            <input
              type="email"
              value={formData.accesscard_email}
              onChange={(e) => setFormData({ ...formData, accesscard_email: e.target.value })}
              required
              disabled={preloaded.accesscard_email}
              style={{ ...styles.input, ...(preloaded.accesscard_email && styles.inputDisabled) }}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Role *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
              disabled={preloaded.role}
              style={{ ...styles.input, ...(preloaded.role && styles.inputDisabled) }}
            >
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>School {!formData.global && '*'}</label>
            <select
              value={formData.school_id}
              onChange={(e) => setFormData({ ...formData, school_id: e.target.value })}
              required={!formData.global}
              disabled={formData.global || preloaded.school_id}
              style={{ ...styles.input, backgroundColor: (formData.global || preloaded.school_id) ? '#f5f5f5' : 'white', ...(preloaded.school_id && styles.inputDisabled) }}
            >
              <option value="">{formData.global ? 'Not required for global' : 'Select a school'}</option>
              {lookups.schools.map(s => <option key={s.school_id} value={s.school_id}>{s.school_name}</option>)}
            </select>
          </div>
          <div style={styles.inputGroup}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={formData.global}
                onChange={(e) => setFormData({ ...formData, global: e.target.checked, school_id: e.target.checked ? '' : formData.school_id })}
                disabled={preloaded.global}
              />
              Global Access
            </label>
          </div>

          {formData.role === 'Student' && (
            <>
              <h3 style={styles.sectionTitle}>Enrollment</h3>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Start Date *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  disabled={preloaded.start_date}
                  style={{ ...styles.input, ...(preloaded.start_date && styles.inputDisabled) }}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  disabled={preloaded.end_date}
                  style={{ ...styles.input, ...(preloaded.end_date && styles.inputDisabled) }}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Grade Level</label>
                <input
                  type="text"
                  value={formData.gradelevel}
                  onChange={(e) => setFormData({ ...formData, gradelevel: e.target.value })}
                  disabled={preloaded.gradelevel}
                  style={{ ...styles.input, ...(preloaded.gradelevel && styles.inputDisabled) }}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Concentration</label>
                <select
                  value={formData.concentration}
                  onChange={(e) => setFormData({ ...formData, concentration: e.target.value })}
                  disabled={preloaded.concentration}
                  style={{ ...styles.input, ...(preloaded.concentration && styles.inputDisabled) }}
                >
                  <option value="">Select</option>
                  {lookups.concentrations.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Association</label>
                <select
                  value={formData.association}
                  onChange={(e) => setFormData({ ...formData, association: e.target.value })}
                  disabled={preloaded.association}
                  style={{ ...styles.input, ...(preloaded.association && styles.inputDisabled) }}
                >
                  <option value="">Select</option>
                  {lookups.associations.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Period</label>
                <select
                  value={formData.period_id}
                  onChange={(e) => setFormData({ ...formData, period_id: e.target.value })}
                  disabled={preloaded.period_id}
                  style={{ ...styles.input, ...(preloaded.period_id && styles.inputDisabled) }}
                >
                  <option value="">Select</option>
                  {lookups.periods.map(p => (
                    <option key={p.period_id} value={p.period_id}>
                      {p.name} ({p.start_date} - {p.end_date})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <button type="submit" disabled={loading} style={styles.submitButton}>
            {loading ? 'Creating...' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ActionLinkPage() {
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleSuccess = (msg) => {
    setSuccess(msg);
    setError(null);
  };

  const handleError = (msg) => {
    setError(msg);
    setSuccess(null);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {success && (
        <div style={styles.centerPage}>
          <div style={styles.card}>
            <h1 style={{ ...styles.title, color: '#28a745' }}>Success</h1>
            <p style={styles.successText}>{success}</p>
            <a href="/login" style={styles.link}>Go to Login</a>
          </div>
        </div>
      )}
      {!success && (
        <>
          {error && (
            <div style={styles.bannerError}>
              {error}
            </div>
          )}
          <ActionLink onSuccess={handleSuccess} onError={handleError} />
        </>
      )}
    </div>
  );
}

const styles = {
  centerPage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 64px)',
    padding: '2rem'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: '2rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: 480
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#212529'
  },
  subtitle: {
    color: '#6c757d',
    marginBottom: '1.5rem',
    fontSize: '0.875rem'
  },
  errorText: {
    color: '#dc3545',
    marginBottom: '1rem'
  },
  successText: {
    color: '#28a745',
    marginBottom: '1rem'
  },
  hint: {
    color: '#6c757d',
    fontSize: '0.875rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  inputGroup: {
    marginBottom: '0.75rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.375rem',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: '#495057'
  },
  input: {
    width: '100%',
    padding: '0.375rem 0.5rem',
    border: '1px solid #dee2e6',
    borderRadius: 4,
    fontSize: '0.875rem'
  },
  inputDisabled: {
    backgroundColor: '#f5f5f5',
    color: '#6c757d',
    cursor: 'not-allowed'
  },
  smallButton: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500
  },
  sectionTitle: {
    marginTop: '1.5rem',
    marginBottom: '0.75rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#495057'
  },
  submitButton: {
    marginTop: '1rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500
  },
  link: {
    color: '#007bff',
    textDecoration: 'none',
    fontWeight: 500
  },
  bannerError: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '0.75rem 1.5rem',
    textAlign: 'center'
  }
};
